import type { Tranch } from "@/types/types";
import SarRecommendedExerciseSchedule from "./SarRecommendedExerciseScheduleGrid";
import SarActualExercisedGrid from "./SarActualExercisedGrid";
import { LoadIndicator, Popup } from "devextreme-react";
import { closePopup } from "@helpers/DxHelper";
import { calculateOptionsVested } from "./sar-utils";
import { formatDate, formatNumber } from "devextreme/localization";
import { useQueryClient } from "@tanstack/react-query";
import { useLTIPTranchDetails } from "@hooks/useLTIPTranchDetails";
import { GridSkeleton, TextSkeleton } from "@/components/Skeletons";
import KeyValueSection from "@/components/KeyValueSection";
import { CrossIcon, RefreshIcon } from "@/components/Icons";
import { PopupRef } from "devextreme-react/cjs/popup";
import PrintSARDetailButton from "./PrintSARDetailButton";

type Props = {
  data: Tranch | undefined;
  tranchCode: string;
  popupRef: React.RefObject<PopupRef>;
};

type SarDetailsData = {
  header: string;
  value: string;
};

let sarDetailsData: SarDetailsData[] = [];

export default function SarDetails({ tranchCode, data, popupRef }: Props) {
  const queryClient = useQueryClient();
  const {
    isFetching,
    isError,
    isRefetching,
    data: tranchDetail,
  } = useLTIPTranchDetails(data?.externalCode);

  if (data) {
    sarDetailsData = [
      {
        header: "Options Granted",
        value: data?.options ? formatNumber(data?.options, "#,##0") : "",
      },
      {
        header: "Options Remaining",
        value: calculateOptionsVested(data)
          ? formatNumber(calculateOptionsVested(data), "#,##0")
          : "",
      },
      {
        header: "Expiration Date",
        value: data?.dateOfExpiration
          ? formatDate(new Date(data?.dateOfExpiration), "MMMM dd, yyyy")
          : "",
      },
    ];
  }

  return (
    <Popup
      ref={popupRef}
      showTitle={false}
      onHiding={() => closePopup(popupRef)}
      onShowing={(e) => {
        const el = e.component.content();
        el.style.padding = "32px";
      }}
      className="flex items-center justify-center print:w-screen"
    >
      {!data ? (
        <LoadIndicator />
      ) : (
        <div className="flex h-full flex-col gap-8 print:relative print:left-0">
          <div className="flex h-7 items-center justify-between">
            <h2 className="line-clamp-5 text-2xl font-medium leading-7">{data.name}</h2>
            <div className="relative flex items-center">
              <button
                className={`shadow-[0px_4px_4px_0px_#00000026 hover:bg-primary relative flex h-10 w-10 items-center justify-center rounded-[4px] transition-all duration-300 hover:shadow-default [&>svg]:transition-all [&>svg]:hover:stroke-white`}
                onClick={() => {
                  queryClient.invalidateQueries({
                    queryKey: ["sars-tranch-details", data?.externalCode],
                  });
                }}
              >
                {RefreshIcon}
              </button>
              <button
                className={`shadow-[0px_4px_4px_0px_#00000026 hover:bg-primary relative left-2 flex h-10 w-10 items-center justify-center rounded-[4px] transition-all duration-300 hover:shadow-default [&>svg]:transition-all [&>svg]:hover:fill-white`}
                onClick={() => closePopup(popupRef)}
              >
                {CrossIcon}
              </button>
            </div>
          </div>
          <KeyValueSection
            items={sarDetailsData}
            sectionHeading=""
            outerContainerClasses="screen:w-[calc(50%-1rem)]"
          />
          {isFetching || isRefetching ? (
            <div className="flex gap-[1.625rem]">
              <div className="flex flex-1 flex-col gap-6">
                <TextSkeleton width={20} />
                <GridSkeleton columns={3} rows={5} />
              </div>
              <div className="flex flex-1 flex-col gap-6">
                <TextSkeleton width={15} />
                <GridSkeleton columns={3} rows={5} />
              </div>
            </div>
          ) : (
            <div className="flex gap-[1.625rem]">
              <SarRecommendedExerciseSchedule recommended={tranchDetail?.data?.recommended} />
              <SarActualExercisedGrid exercised={tranchDetail?.data?.exercised} />
            </div>
          )}
          {isError ? <div>Failed refetching SAR details</div> : null}
          <PrintSARDetailButton externalCode={tranchCode} detailCode={data?.externalCode} trancheName={data.name} />
        </div>
      )}
    </Popup>
  );
}
